// Per-space folders cache. Keyed by spaceId so the sidebar can show folders
// for multiple spaces simultaneously. Mirrors the style of store/spaces.ts and
// store/cycles.ts but uses a Record<spaceId, SpaceFolderEntry> as the signal
// value instead of a single-id flat state.

import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { ApiFolder } from "@juuplan/shared";
import {
  listFolders,
  createFolder as apiCreateFolder,
  updateFolder as apiUpdateFolder,
  deleteFolder as apiDeleteFolder,
  type FolderCreate,
  type FolderUpdate,
} from "../lib/api";

export interface SpaceFolderEntry {
  /** Sorted by sort_order asc, then id asc. */
  items: ApiFolder[];
  loading: boolean;
  /** True once at least one successful fetch has completed for this spaceId. */
  loaded: boolean;
  error: string | null;
}

/** Global per-space cache. */
const foldersSignal = signal<Record<number, SpaceFolderEntry>>({});

/** Guards against duplicate in-flight loads per spaceId. */
const inFlight = new Map<number, Promise<void>>();

function sortFolders(items: ApiFolder[]): ApiFolder[] {
  return [...items].sort(
    (a, b) => a.sort_order - b.sort_order || a.id - b.id
  );
}

async function loadFolders(spaceId: number): Promise<void> {
  // Set loading state for this space while preserving other spaces.
  foldersSignal.value = {
    ...foldersSignal.value,
    [spaceId]: {
      items: foldersSignal.value[spaceId]?.items ?? [],
      loading: true,
      loaded: foldersSignal.value[spaceId]?.loaded ?? false,
      error: null,
    },
  };
  try {
    const raw = await listFolders(spaceId);
    foldersSignal.value = {
      ...foldersSignal.value,
      [spaceId]: { items: sortFolders(raw), loading: false, loaded: true, error: null },
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    foldersSignal.value = {
      ...foldersSignal.value,
      [spaceId]: {
        items: foldersSignal.value[spaceId]?.items ?? [],
        loading: false,
        loaded: false,
        error: message,
      },
    };
  } finally {
    inFlight.delete(spaceId);
  }
}

/**
 * Hook: subscribe to the folders for a given space.
 * Triggers the initial load on first use; subsequent renders return from cache.
 * Guards against duplicate in-flight loads with the inFlight Map.
 */
export function useFolders(spaceId: number): SpaceFolderEntry {
  useEffect(() => {
    const entry = foldersSignal.value[spaceId];
    if (!inFlight.has(spaceId) && (!entry || (!entry.loaded && !entry.loading))) {
      inFlight.set(spaceId, loadFolders(spaceId));
    }
  }, [spaceId]);

  return (
    foldersSignal.value[spaceId] ?? {
      items: [],
      loading: true,
      loaded: false,
      error: null,
    }
  );
}

/** Force a fresh fetch for a given space — use after any folder mutation. */
export function refreshFolders(spaceId: number): Promise<void> {
  const p = loadFolders(spaceId);
  inFlight.set(spaceId, p);
  return p;
}

// ---------- CRUD helpers ----------

/** Create a folder in a space and refresh the cache. */
export async function addFolder(
  spaceId: number,
  body: FolderCreate
): Promise<ApiFolder> {
  const created = await apiCreateFolder(spaceId, body);
  await refreshFolders(spaceId);
  return created;
}

/** Update a folder and refresh the space's cache. */
export async function patchFolder(
  folderId: number,
  spaceId: number,
  body: FolderUpdate
): Promise<ApiFolder> {
  const updated = await apiUpdateFolder(folderId, body);
  await refreshFolders(spaceId);
  return updated;
}

/** Delete a folder and remove it from the space's cache. */
export async function removeFolder(
  folderId: number,
  spaceId: number
): Promise<void> {
  await apiDeleteFolder(folderId);
  const entry = foldersSignal.value[spaceId];
  if (entry) {
    foldersSignal.value = {
      ...foldersSignal.value,
      [spaceId]: {
        ...entry,
        items: entry.items.filter((f) => f.id !== folderId),
      },
    };
  }
}
